import { Fragment, type ReactNode } from 'react';
import Link from 'next/link';
import Tooltip from '@components/Presentation/atoms/Tooltip';

export type LinkSection = {
  name: string;
  url?: string;
  icon: ReactNode;
  isExternal?: boolean;
  iconRight?: ReactNode;
  onClick?: () => void;
  className?: string;
  show?: boolean;
};

interface NavLinksProps {
  linkSections: LinkSection[][];
  isExpanded: boolean;
  pathname: string | null;
}

export default function NavLinks({ linkSections, isExpanded, pathname }: NavLinksProps) {
  const getActiveLinkClass = (link: string, defaultClass: string, activeClass: string) => {
    if (!link) return defaultClass;
    if (pathname === '/' && link === '/') return activeClass;
    return (pathname !== '/' && link !== '/' && pathname?.startsWith(link)) ? activeClass : defaultClass;
  };

  return (
    <div className={`${isExpanded ? 'px-6' : 'flex flex-col items-center'} mt-4 text-sm`}>
      {linkSections.map((section, index) => (
        <div key={index} className={'flex flex-col gap-1 mb-1'}>
          {section.map((link, linkIndex) => {
            if (!link.show) return null;
            return (
              <Fragment key={linkIndex}>
                <Tooltip key={link.name}
                  content={link.name}
                  placement='left'
                  isDisabled={isExpanded}
                >
                  <Link
                    href={link.url || '#'}
                    onClick={link?.onClick || (() => { })}
                    className={`rounded-md flex items-center px-2 py-1.5 h-7 gap-4 ${getActiveLinkClass(link?.url || '', 'text-default-700 hover:bg-content3', 'bg-primary-500/20 text-primary-500')}`}
                    target={link?.isExternal ? '_blank' : undefined}
                  >
                    <div className={`${link?.className}  w-5 h-5 flex items-center`}>
                      {link.icon}
                    </div>
                    {isExpanded && <span className='text-sm'>{link.name}</span>}
                    {isExpanded && link.iconRight && <div className='ml-auto flex'>{link.iconRight}</div>}
                  </Link>
                </Tooltip>
              </Fragment>
            );
          })}
          {index !== linkSections.length - 1 && linkSections[index]?.some(link => link.show) && (
            <div className='h-px bg-divider my-2 mx-2' />
          )}
        </div>
      ))}
    </div>
  );
}
